<script setup lang="ts">
/**
 * Champ ADRESSE des formulaires publics (adhésion, billetterie, livraison de
 * la boutique, adresse du reçu après un don).
 *
 * Suggestions (décision de Karim du 28/09/2026 au soir, contrat §18.33) :
 *  · route PROTÉGÉE de l'API quand elle est allumée
 *    (`useSessionAdressesPubliques`) : Google Places d'abord — fautes de
 *    frappe, lieux nommés, monde entier —, secours BAN / OpenStreetMap côté
 *    serveur. Anti-robots Cloudflare Turnstile invisible, UNE fois par
 *    session, préparé au premier focus ;
 *  · BAN DIRECTE depuis le navigateur sinon (interrupteur éteint, API
 *    injoignable, 404 pendant un déploiement, 429). Jamais d'écran vide.
 *
 * Par défaut la valeur émise est une CHAÎNE (l'adresse complète) : les
 * réponses aux champs personnalisés sont stockées en JSON libre. Avec
 * `valeur="rue"`, le champ ne garde que la rue et le parent reçoit le code
 * postal et la ville par `select` — livraison boutique, stockée en trois
 * morceaux. Le contrat de `select` et du mode `rue` est INCHANGÉ (tunnel
 * boutique : seul ce champ est touché, jamais la logique de commande).
 *
 * CLIC INSTANTANÉ (même règle que le dashboard et les apps, §18.32.1) :
 *  · BAN / OpenStreetMap : suggestion déjà complète, choisie aussitôt ;
 *  · Google : la liste se ferme et le champ AFFICHE tout de suite la
 *    suggestion (atténuée, `aria-busy`) pendant la vérification (`/details`,
 *    moins d'une seconde) ; le modèle garde le TEXTE TAPÉ jusqu'à l'adresse
 *    vérifiée, qui le remplace. Un nom de lieu Google (« Grande Mosquée de
 *    Lyon ») n'est donc JAMAIS enregistré comme adresse : valider pendant la
 *    vérification envoie le texte tapé. En mode `rue`, code postal et ville
 *    de l'adresse PRÉCÉDENTE sont effacés dès le clic (jamais une rue neuve
 *    avec un ancien code postal), puis remplis par l'adresse vérifiée.
 *    Échec : le texte tapé reste (`texteChoixSansDetails`). Frappe pendant la
 *    vérification : le choix est abandonné.
 *
 * Attribution sous la liste (§18.11) : logo officiel Google Maps (gris, blanc
 * en sombre, 18 px) ou « © OpenStreetMap » avec son lien ; rien pour la BAN.
 * Liste masquée pour PostHog (`ph-no-capture`, `data-ph-mask`) : adresses
 * personnelles et contenu Google n'entrent jamais dans l'analytique.
 *
 * ⚠️ La saisie libre reste toujours possible. La liste est sortie du flux
 * (Teleport + `fixed`) : un conteneur en `overflow: hidden` la rognait.
 * Placement par `placerListeSuggestions` (@donoor/utils, règle commune au
 * dashboard) : sous le champ si la place y est, sinon AU-DESSUS, hauteur
 * bornée à l'écran — un champ en bas de page (reçu fiscal de la page de
 * succès) ouvrait la liste sous le bord de l'écran, coupée, 30/09/2026.
 */
import type { AdresseStructuree, AttributionSuggestions, SuggestionAdresse } from "@donoor/types/adresse";
import {
  attributionAffichee,
  DELAI_FRAPPE_MS,
  jetonSessionRecherche,
  placementListeInitial,
  placerListeSuggestions,
  requeteRecherche,
  rueDepuisSuggestion,
  styleListeSuggestions,
  texteChoixSansDetails,
  zoneVisibleFenetre,
} from "@donoor/utils";
import { adressesPubliquesApi, versAddressSuggestion, type AddressSuggestion } from "~/services/api/addressLookup";
import { BORDURE_CHAMP_PUBLIC, BORDURE_CHAMP_PUBLIC_ERREUR } from "~/utils/stylesChamps";

const { t } = useI18n();

const modele = defineModel<string | undefined>();

const props = withDefaults(
  defineProps<{
    placeholder?: string;
    enErreur?: boolean;
    id?: string;
    /** « complete » : adresse entière ; « rue » : numéro + voie seulement. */
    valeur?: "complete" | "rue";
    /** Attribut `autocomplete` du navigateur (remplissage automatique). */
    autocomplete?: string;
    /** Focus en bordure seule, sans halo (tunnel boutique). */
    sansAnneau?: boolean;
    ariaDescribedby?: string;
  }>(),
  {
    placeholder: undefined,
    enErreur: false,
    id: undefined,
    ariaDescribedby: undefined,
    valeur: "complete",
    autocomplete: "street-address",
    sansAnneau: false,
  },
);

const emit = defineEmits<{
  /** Une suggestion a été choisie — morceaux structurés inclus. */
  (e: "select", s: AddressSuggestion): void;
  (e: "blur"): void;
}>();

const { active: routeProtegee, apiBase, session } = useSessionAdressesPubliques();
/** Pays de l'association de la page : oriente les suggestions Google. */
const paysAssociation = usePaysAssociation();

const racine = ref<HTMLElement | null>(null);
const champ = ref<HTMLInputElement | null>(null);
const liste = ref<HTMLElement | null>(null);
const suggestions = ref<SuggestionAdresse[]>([]);
const attribution = ref<AttributionSuggestions | null>(null);
const mention = computed(() => attributionAffichee(attribution.value));
const recherche = ref(false);
const ouvert = ref(false);
/** Ligne surlignée au clavier (-1 = aucune). */
const actif = ref(-1);
const idListe = useId();
/** Suggestion Google en cours de vérification : AFFICHÉE seulement, jamais dans le modèle. */
const affichage = ref<string | null>(null);
const verification = computed(() => affichage.value !== null);
/** Annonce pour les lecteurs d'écran. */
const annonce = ref("");

defineExpose({
  focus: () => champ.value?.focus(),
});

let minuteur: ReturnType<typeof setTimeout> | undefined;
/** Numéro de la dernière opération : une réponse tardive n'écrase jamais une plus récente. */
let generation = 0;
/** Session de facturation Google (UUID v4) : de la première frappe au choix. */
let sessionRecherche: string | null = null;
/** Dernier texte TAPÉ (repli d'un choix Google sans détails). */
let saisie = "";

function fermer() {
  ouvert.value = false;
  actif.value = -1;
}

/** Suggestions : route protégée si allumée (secours BAN directe si elle ne répond pas). */
async function chercher(texte: string): Promise<{ suggestions: SuggestionAdresse[]; attribution: AttributionSuggestions | null }> {
  const s = session();
  if (routeProtegee && s) {
    sessionRecherche ??= jetonSessionRecherche();
    const jeton = await s.jeton();
    const r = await adressesPubliquesApi.rechercher(apiBase, texte, sessionRecherche, {
      jeton,
      pays: paysAssociation.value,
    });
    if (r) {
      if (r.sessionRefusee) s.invalider();
      return { suggestions: r.suggestions, attribution: r.attribution };
    }
  }
  return { suggestions: await adressesPubliquesApi.banDirecte(texte), attribution: null };
}

function onFocus() {
  ouvert.value = suggestions.value.length > 0;
  // Premier focus : le jeton anti-robots se prépare sans rien bloquer.
  if (routeProtegee) session()?.prechauffer();
}

function onSaisie(e: Event) {
  const valeur = (e.target as HTMLInputElement).value;
  modele.value = valeur;
  saisie = valeur;
  // Une frappe abandonne un choix Google en cours de vérification.
  const numero = ++generation;
  affichage.value = null;

  if (minuteur) clearTimeout(minuteur);
  // Même borne que l'appel (`requeteRecherche` : 3 à 200 caractères, blancs
  // repliés) : hors borne, aucune recherche et la liste se ferme.
  if (requeteRecherche(valeur) === null) {
    suggestions.value = [];
    attribution.value = null;
    recherche.value = false;
    fermer();
    return;
  }

  minuteur = setTimeout(async () => {
    recherche.value = true;
    const r = await chercher(valeur);
    if (numero !== generation) return; // une frappe plus récente a pris la main
    suggestions.value = r.suggestions;
    attribution.value = r.suggestions.length ? r.attribution : null;
    actif.value = -1;
    ouvert.value = r.suggestions.length > 0;
    recherche.value = false;
    annonce.value = r.suggestions.length ? `${r.suggestions.length} suggestion${r.suggestions.length > 1 ? "s" : ""}` : "";
  }, DELAI_FRAPPE_MS);
}

/** Choix d'une adresse complète (BAN, OpenStreetMap, ou Google vérifiée). */
function appliquer(s: SuggestionAdresse, adresse: AdresseStructuree | null) {
  modele.value =
    props.valeur === "rue"
      ? rueDepuisSuggestion(s, adresse ? { libelle: adresse.libelle, adresse } : null) || modele.value
      : adresse?.libelle || s.libelle;
  emit("select", versAddressSuggestion(s, adresse));
}

async function choisir(s: SuggestionAdresse) {
  const numero = ++generation;
  if (minuteur) clearTimeout(minuteur);
  recherche.value = false;
  suggestions.value = [];
  attribution.value = null;
  fermer();

  if (s.fournisseur !== "google") {
    sessionRecherche = null;
    appliquer(s, s.adresse);
    return;
  }

  // Google : affichage immédiat, vérification en arrière-plan.
  affichage.value = props.valeur === "rue" ? s.principal || s.libelle : s.libelle;
  annonce.value = t("association.champsFormulaire.verificationAdresse");
  if (props.valeur === "rue") {
    // Jamais une rue neuve avec le code postal de l'adresse précédente.
    emit("select", { label: saisie, value: saisie, street: saisie, postcode: "", city: "" });
  }
  const jetonSession = sessionRecherche ?? jetonSessionRecherche();
  const adresse = await adressesPubliquesApi.details(apiBase, s.id, jetonSession, session()?.actuel() ?? null);
  // La session Google se termine avec les détails.
  sessionRecherche = null;
  if (numero !== generation) return; // frappe ou autre choix entre-temps
  affichage.value = null;
  annonce.value = "";
  if (adresse) {
    appliquer(s, adresse);
  } else {
    // Sans détails, rien ne distingue une adresse d'un nom : le texte tapé reste.
    modele.value = texteChoixSansDetails(s, saisie);
  }
}

function auClavierChamp(e: KeyboardEvent) {
  const n = suggestions.value.length;
  if (!ouvert.value || !n) return;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    actif.value = (actif.value + 1) % n;
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    actif.value = actif.value <= 0 ? n - 1 : actif.value - 1;
  } else if (e.key === "Enter" && actif.value >= 0) {
    e.preventDefault();
    void choisir(suggestions.value[actif.value]!);
  } else if (e.key === "Escape") {
    e.preventDefault();
    fermer();
  }
}

// ─── Position de la liste (sortie du flux) ─────────────────────────────
// Dessous si la place y est, sinon dessus ; hauteur bornée à la zone visible
// (clavier mobile compris). Recalculée à l'ouverture, au défilement et au
// redimensionnement.
const placement = ref(placementListeInitial());
const styleListe = computed(() => styleListeSuggestions(placement.value));
/** Glissement d'ouverture dans le sens où la liste pousse. */
const glissementFerme = computed(() =>
  placement.value.cote === "haut" ? "opacity-0 translate-y-1" : "opacity-0 -translate-y-1",
);

function majPosition() {
  const r = champ.value?.getBoundingClientRect();
  if (!r) return;
  placement.value = placerListeSuggestions(r, zoneVisibleFenetre(window));
}

watch(ouvert, (o) => {
  if (o) majPosition();
});

/** Clic hors du champ ET hors de la liste (qui vit dans <body>). */
function auClicDehors(e: MouseEvent) {
  if (!ouvert.value) return;
  const cible = e.target as Node;
  if (racine.value?.contains(cible) || liste.value?.contains(cible)) return;
  fermer();
}

function auDefilement() {
  if (ouvert.value) majPosition();
}

onMounted(() => {
  document.addEventListener("mousedown", auClicDehors);
  window.addEventListener("scroll", auDefilement, true);
  window.addEventListener("resize", auDefilement);
  // Clavier mobile qui s'ouvre ou se ferme : seule la fenêtre visuelle bouge.
  window.visualViewport?.addEventListener("resize", auDefilement);
  window.visualViewport?.addEventListener("scroll", auDefilement);
});
onUnmounted(() => {
  generation++;
  if (minuteur) clearTimeout(minuteur);
  document.removeEventListener("mousedown", auClicDehors);
  window.removeEventListener("scroll", auDefilement, true);
  window.removeEventListener("resize", auDefilement);
  window.visualViewport?.removeEventListener("resize", auDefilement);
  window.visualViewport?.removeEventListener("scroll", auDefilement);
});

// Focus : UNE bordure, noire — même règle sur toutes les pages publiques
// (cf. utils/stylesChamps). Plus d'anneau, avec ou sans `sansAnneau`.
const classesEtat = computed(() =>
  props.enErreur ? BORDURE_CHAMP_PUBLIC_ERREUR : BORDURE_CHAMP_PUBLIC,
);
</script>

<template>
  <div ref="racine" class="relative">
    <div class="relative">
      <UIcon
        name="i-lucide-map-pin"
        class="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-gray-400 pointer-events-none"
      />
      <input
        :id="props.id"
        ref="champ"
        :value="affichage ?? modele ?? ''"
        type="text"
        :autocomplete="props.autocomplete"
        :placeholder="props.placeholder ?? $t('association.champsFormulaire.adressePlaceholder')"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="ouvert && suggestions.length > 0"
        :aria-controls="idListe"
        :aria-activedescendant="actif >= 0 ? `${idListe}-${actif}` : undefined"
        :aria-invalid="props.enErreur || undefined"
        :aria-describedby="props.ariaDescribedby"
        :aria-busy="verification || undefined"
        class="w-full min-h-11 pl-10 pr-10 py-3 rounded-xl border bg-white dark:bg-[#0f0f0f] text-sm placeholder-gray-400 focus:outline-none transition-colors"
        :class="[classesEtat, verification ? 'text-gray-400 dark:text-gray-500' : 'text-[#2d2e5c] dark:text-white']"
        data-testid="champ-adresse-public"
        @input="onSaisie"
        @keydown="auClavierChamp"
        @focus="onFocus"
        @blur="emit('blur')"
      />
      <UIcon
        v-if="recherche || verification"
        name="i-lucide-loader-2"
        class="absolute right-3.5 top-1/2 -translate-y-1/2 size-4 text-gray-400 animate-spin"
        aria-hidden="true"
      />
    </div>
    <span class="sr-only" role="status" aria-live="polite">{{ annonce }}</span>

    <!-- `mousedown.prevent` : garde le focus dans le champ pendant le clic. -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-150 ease-out"
        :enter-from-class="glissementFerme"
        leave-active-class="transition duration-100 ease-in"
        :leave-to-class="glissementFerme"
      >
        <div
          v-if="ouvert && suggestions.length > 0"
          ref="liste"
          class="ph-no-capture fixed z-[9999] flex flex-col rounded-xl border border-gray-200 dark:border-[#262626] bg-white dark:bg-[#0f0f0f] shadow-xl overflow-hidden"
          :style="styleListe"
          :data-cote="placement.cote"
          data-ph-mask
          data-testid="liste-suggestions-public"
          @mousedown.prevent
        >
          <ul :id="idListe" role="listbox" class="min-h-0 max-h-60 overflow-y-auto overscroll-contain">
            <li
              v-for="(s, i) in suggestions"
              :id="`${idListe}-${i}`"
              :key="s.id"
              role="option"
              :aria-selected="i === actif"
              class="px-4 py-3 text-sm text-[#2d2e5c] dark:text-gray-200 cursor-pointer transition-colors flex items-start gap-2.5"
              :class="i === actif ? 'bg-gray-50 dark:bg-[#1a1a1a]' : 'hover:bg-gray-50 dark:hover:bg-[#1a1a1a]'"
              @mouseenter="actif = i"
              @click="choisir(s)"
            >
              <UIcon name="i-lucide-map-pin" class="size-4 text-gray-400 shrink-0 mt-0.5" />
              <span class="min-w-0">
                <span class="block truncate">{{ s.principal || s.libelle }}</span>
                <span v-if="s.secondaire" class="block truncate text-xs text-gray-500 dark:text-gray-400">{{
                  s.secondaire
                }}</span>
              </span>
            </li>
          </ul>
          <!-- Attribution (§18.11), hors de la liste d'options. -->
          <p
            v-if="mention?.url"
            class="px-4 py-1.5 text-right text-xs text-[#5E5E5E] dark:text-white border-t border-gray-100 dark:border-[#262626]"
            translate="no"
          >
            <a
              :href="mention.url"
              target="_blank"
              rel="noopener noreferrer"
              class="hover:underline"
              data-testid="attribution-osm"
            >{{ mention.texte }}</a>
          </p>
          <div
            v-else-if="mention"
            class="flex justify-end px-2.5 pt-2.5 pb-[5px] border-t border-gray-100 dark:border-[#262626]"
            data-testid="attribution-google-maps"
          >
            <img
              src="/attribution/google-maps-gris.svg"
              :alt="mention.texte"
              width="98"
              height="18"
              class="h-[18px] w-auto dark:hidden"
              data-testid="logo-google-maps"
            >
            <img
              src="/attribution/google-maps-blanc.svg"
              alt=""
              aria-hidden="true"
              width="98"
              height="18"
              class="hidden h-[18px] w-auto dark:block"
            >
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
